import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowUpRight, ChevronDown, ExternalLink, Menu, X } from "lucide-react";
import { LOGOS, NAV, PARENT_SITE } from "../lib/site";
import { LogoChip } from "./blocks";

function Dropdown({ item, onNavigate }) {
  return (
    <div className="relative group shrink-0">
      <Link
        to={item.to}
        data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[14px] xl:text-[15px] font-medium text-white/85 hover:text-lime transition-colors duration-200 whitespace-nowrap shrink-0"
        aria-haspopup="true"
      >
        <span className="whitespace-nowrap">{item.label}</span>
        <ChevronDown size={14} className="opacity-60 transition-transform duration-200 group-hover:rotate-180 shrink-0" aria-hidden="true" />
      </Link>
      <div
        className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:visible group-focus-within:opacity-100 group-focus-within:translate-y-0 transition-all duration-200 absolute left-0 top-full pt-2 z-50"
        role="menu"
      >
        <div className="w-72 bg-charcoal border border-white/15 shadow-2xl p-2">
          {item.children.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              role="menuitem"
              onClick={onNavigate}
              className="block px-4 py-3 hover:bg-white/5 transition-colors duration-150"
            >
              <span className="block text-sm font-semibold text-white">{c.label}</span>
              {c.desc ? <span className="block text-xs text-softgrey mt-0.5">{c.desc}</span> : null}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileMenu({ open, onClose }) {
  const { pathname } = useLocation();
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 bg-ink/98 backdrop-blur-md overflow-y-auto" data-testid="mobile-menu">
      <div className="shell pt-28 pb-12">
        <nav aria-label="Mobile navigation">
          <ul className="divide-y divide-white/10">
            {NAV.map((item) => (
              <li key={item.label} className="py-1">
                {item.children ? (
                  <details className="group">
                    <summary className="flex items-center justify-between py-4 cursor-pointer list-none font-display font-bold text-2xl text-white">
                      {item.label}
                      <ChevronDown size={20} className="text-lime transition-transform group-open:rotate-180" aria-hidden="true" />
                    </summary>
                    <div className="pb-4">
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          onClick={onClose}
                          className={`block py-2.5 pl-4 text-base ${
                            pathname === c.to ? "text-lime" : "text-softgrey"
                          }`}
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </details>
                ) : (
                  <Link
                    to={item.to}
                    onClick={onClose}
                    className={`block py-4 font-display font-bold text-2xl ${
                      pathname === item.to ? "text-lime" : "text-white"
                    }`}
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>
        <div className="mt-8 flex flex-col gap-3">
          <Link
            to="/contact"
            onClick={onClose}
            data-testid="mobile-cta-consultation"
            className="bg-lime text-charcoal font-semibold text-center px-7 py-4"
          >
            Request Consultation
          </Link>
          <a
            href={PARENT_SITE}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-global-link"
            className="border border-white/25 text-white font-semibold text-center px-7 py-4 inline-flex items-center justify-center gap-2"
          >
            Softline Global (Sweden) <ExternalLink size={15} />
          </a>
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const close = () => setOpen(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 bg-black transition-shadow duration-200 ${scrolled ? "shadow-[0_1px_0_rgba(255,255,255,0.12),0_8px_30px_rgba(0,0,0,0.5)]" : ""}`}
      data-testid="site-header"
    >
      <div className="w-full max-w-[88rem] mx-auto px-5 sm:px-8 md:px-10 flex items-center justify-between h-[72px] md:h-20">
        <Link to="/" aria-label="Softline Nigeria — home" data-testid="header-logo-link" onClick={close} className="flex items-center shrink-0 mr-8 lg:mr-12">
          <LogoChip size="h-7 sm:h-8 md:h-9" />
        </Link>

        <div className="hidden lg:flex items-center gap-5 xl:gap-7 ml-auto">
          <nav aria-label="Primary navigation" className="flex items-center gap-0.5 xl:gap-1">
            {NAV.map((item) =>
              item.children ? (
                <Dropdown key={item.label} item={item} onNavigate={close} />
              ) : (
                <NavLink
                  key={item.label}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-3 py-2 text-[14px] xl:text-[15px] font-medium whitespace-nowrap transition-colors duration-200 shrink-0 ${
                      isActive ? "text-lime" : "text-white/85 hover:text-lime"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-3 shrink-0 pl-3 border-l border-white/15">
            <a
              href={PARENT_SITE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="global-link"
              aria-label="Softline global website — Sweden"
              className="inline-flex items-center gap-1.5 text-white/60 hover:text-lime transition-colors duration-200 font-mono text-[11px] uppercase tracking-[0.2em] whitespace-nowrap shrink-0"
            >
              Global <ExternalLink size={13} />
            </a>
            <Link
              to="/contact"
              data-testid="header-cta-consultation"
              className="bg-lime text-charcoal font-semibold px-4 xl:px-5 py-2.5 text-xs xl:text-sm hover:bg-limelight transition-colors duration-200 whitespace-nowrap shrink-0"
            >
              Request Consultation
            </Link>
          </div>
        </div>

        <button
          className="lg:hidden text-white p-2 -mr-2 ml-auto"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
      {open ? <MobileMenu open={open} onClose={close} /> : null}
    </header>
  );
}
